import { Injectable } from '@angular/core';
import { Event } from '@api/models/event/event';
import {
  EventsHttpService,
  EventsOrderType,
  EventsTargetType,
  GetEventsByDateRequest,
} from '@api/http/events/events-http.service';
import {
  PickerItem,
  PickerItemType,
} from '@lib-modules/picker/components/picker-item/picker-item.component';
import { BaseCardService } from '@lib-modules/cards/services/base-card/base-card.service';
import { EntityType } from '@lib/models/entity/entity-type';

export enum EventContentType {
  EventsByDate = 'EventsByDate',
  ActiveEvents = 'active_events',
  UpcomingEvents = 'upcoming_events',
  PastEvents = 'past_events',
}

@Injectable({
  providedIn: 'root',
})
export class EventCardService extends BaseCardService {
  constructor(private eventsHttp: EventsHttpService) {
    super();
  }

  // Matches
  public async fetchData(
    contentType: EventContentType,
    item?: PickerItem,
    skip = 0,
    limit = 12,
    date?: Date
  ): Promise<Event[]> {
    this.init(skip, limit, item);

    switch (contentType) {
      case EventContentType.EventsByDate:
        return this.getEventsByDate(date);
      case EventContentType.ActiveEvents:
        return this.getActiveEvents();
      case EventContentType.UpcomingEvents:
        return this.getEvents(EventsOrderType.Future);
      case EventContentType.PastEvents:
        return this.getEvents(EventsOrderType.Past);
    }
  }

  private async getEventsByDate(date: Date): Promise<Event[]> {
    const filter = this.type ?? 'global';
    const options: GetEventsByDateRequest = {
      filter,
      date,
      skip: this.skip,
      limit: this.limit,
    };
    if (this.type && this.type !== PickerItemType.All) {
      options.entityId = this.id;
    }
    const response = await this.eventsHttp.getEventsByDate(options);
    const liveResponse = await this.eventsHttp.getEventsByDate({
      ...options,
      live: true,
    });
    let events = [];
    let liveEvents = [];
    if (response.data) {
      events = response.data.map((event) => new Event(event));
    }
    if (liveResponse.data) {
      liveEvents = liveResponse.data.map((event) => new Event(event));
    }
    // Filter duplicates
    const liveIds = new Set(liveEvents.map((event) => event._id));
    const filteredEvents = events.filter((event) => !liveIds.has(event._id));
    return liveEvents.concat(filteredEvents);
  }

  private mapPickerTypeToEntityType(pickerType: PickerItemType): EntityType {
    switch (pickerType) {
      case PickerItemType.Team:
        return EntityType.Team;
      case PickerItemType.League:
        return EntityType.League;
      case PickerItemType.State:
        return EntityType.State;
      default:
        throw new Error(`Unsupported PickerItemType: ${pickerType}`);
    }
  }

  private async getActiveEvents(): Promise<Event[]> {
    const [pastResponse, futureResponse] = await Promise.all([
      this.getEvents(EventsOrderType.Past),
      this.getEvents(EventsOrderType.Future, this.getFutureEventsLimit()),
    ]);

    return [...(futureResponse.reverse() || []), ...(pastResponse || [])];
  }

  private getFutureEventsLimit(): number {
    switch (this.type) {
      case PickerItemType.League:
        return 4;
      case PickerItemType.Team:
        return 1;
    }
    return this.limit;
  }

  private async getEvents(
    order: EventsOrderType,
    limitOverride?: number
  ): Promise<Event[]> {
    const response = await this.eventsHttp.getEventsForTargetPaginated(
      this.type === PickerItemType.Team
        ? EventsTargetType.Team
        : EventsTargetType.League,
      this.id,
      order,
      this.skip,
      limitOverride || this.limit
    );
    return response?.data || [];
  }
}
